import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { propertiesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Image as ImgIcon, Video, Sparkles,
  Moon, Sun, Eye, ExternalLink, GripVertical,
} from 'lucide-react';

const blankOption = () => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  price: 0,
  image_url: '',
  enabled: true,
  order: 0,
});

const blankMedia = () => ({
  id: crypto.randomUUID(),
  url: '',
  type: 'image',
  alt: '',
  order: 0,
});

export default function PropertyEditorPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    propertiesApi.getAll()
      .then(res => {
        const p = res.data.find(x => x.id === propertyId);
        if (!p) {
          toast.error('Propriété introuvable');
          navigate('/admin/proprietes');
          return;
        }
        // Ensure new fields have defaults
        setData({
          name: p.name || '',
          address: p.address || '',
          property_type: p.property_type || 'appartement',
          bedrooms: p.bedrooms ?? 1,
          bathrooms: p.bathrooms ?? 1,
          max_guests: p.max_guests ?? 2,
          base_price: p.base_price ?? 100,
          cleaning_fee: p.cleaning_fee ?? 0,
          description: p.description || '',
          long_description: p.long_description || '',
          tagline: p.tagline || '',
          image_url: p.image_url || '',
          night_enabled: p.night_enabled !== false,
          night_check_in_hour: p.night_check_in_hour || '18:00',
          night_check_out_hour: p.night_check_out_hour || '10:00',
          night_price: p.night_price ?? p.base_price ?? 100,
          dayuse_enabled: p.dayuse_enabled !== false,
          dayuse_start_hour: p.dayuse_start_hour || '12:00',
          dayuse_end_hour: p.dayuse_end_hour || '15:00',
          dayuse_price: p.dayuse_price ?? 120,
          hero_video_url: p.hero_video_url || '',
          hero_image_url: p.hero_image_url || '',
          media: p.media || [],
          options: p.options || [],
        });
      })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [propertyId, navigate]);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await propertiesApi.update(propertyId, {
        ...data,
        bedrooms: parseInt(data.bedrooms) || 1,
        bathrooms: parseInt(data.bathrooms) || 1,
        max_guests: parseInt(data.max_guests) || 2,
        base_price: parseFloat(data.base_price) || 0,
        cleaning_fee: parseFloat(data.cleaning_fee) || 0,
        night_price: parseFloat(data.night_price) || 0,
        dayuse_price: parseFloat(data.dayuse_price) || 0,
      });
      toast.success('Propriété sauvegardée');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="p-8 text-center text-muted-foreground">Chargement…</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-editor">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/proprietes')} data-testid="back-properties">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-display tracking-tight">{data.name || 'Édition de la suite'}</h1>
            <p className="text-muted-foreground text-sm mt-1">Sessions, rituels, médias</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => window.open(`/reserver/${propertyId}`, '_blank')} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-2" /> Aperçu public
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-property-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-card border border-border/40 w-full justify-start overflow-x-auto h-auto p-1">
          <TabsTrigger value="general" data-testid="tab-general">Général</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions & Tarifs</TabsTrigger>
          <TabsTrigger value="hero" data-testid="tab-hero">Vitrine</TabsTrigger>
          <TabsTrigger value="options" data-testid="tab-options">Rituels</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Galerie</TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general" className="mt-6 space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input value={data.name} onChange={(e) => set('name', e.target.value)} data-testid="prop-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input value={data.address} onChange={(e) => set('address', e.target.value)} data-testid="prop-address-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Chambres</Label>
                  <Input type="number" value={data.bedrooms} onChange={(e) => set('bedrooms', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Salles de bain</Label>
                  <Input type="number" value={data.bathrooms} onChange={(e) => set('bathrooms', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Capacité</Label>
                  <Input type="number" value={data.max_guests} onChange={(e) => set('max_guests', e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description courte</Label>
                <Textarea rows={2} value={data.description} onChange={(e) => set('description', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Description longue (page publique)</Label>
                <Textarea rows={4} value={data.long_description} onChange={(e) => set('long_description', e.target.value)} placeholder="Un cocon nocturne pensé pour les sens..." />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SESSIONS */}
        <TabsContent value="sessions" className="mt-6 space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-6">
              <div className="flex items-center justify-between p-4 border border-border/40">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-xl">Session Nuit</h3>
                    <p className="text-xs text-muted-foreground">Configurer la session nuit complète</p>
                  </div>
                </div>
                <Switch checked={data.night_enabled} onCheckedChange={(v) => set('night_enabled', v)} data-testid="night-enabled-switch" />
              </div>
              {data.night_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-4 border-l border-primary/30">
                  <div className="space-y-2">
                    <Label>Heure d'arrivée</Label>
                    <Input type="time" value={data.night_check_in_hour} onChange={(e) => set('night_check_in_hour', e.target.value)} data-testid="night-checkin-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Heure de départ</Label>
                    <Input type="time" value={data.night_check_out_hour} onChange={(e) => set('night_check_out_hour', e.target.value)} data-testid="night-checkout-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix par nuit (€)</Label>
                    <Input type="number" step="1" value={data.night_price} onChange={(e) => set('night_price', e.target.value)} data-testid="night-price-input" />
                  </div>
                  <div className="space-y-2 md:col-span-3">
                    <Label>Frais de ménage (€) — facultatif</Label>
                    <Input type="number" step="1" value={data.cleaning_fee} onChange={(e) => set('cleaning_fee', e.target.value)} data-testid="cleaning-fee-input" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-6">
              <div className="flex items-center justify-between p-4 border border-border/40">
                <div className="flex items-center gap-3">
                  <Sun className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-xl">Day Use</h3>
                    <p className="text-xs text-muted-foreground">Session de jour</p>
                  </div>
                </div>
                <Switch checked={data.dayuse_enabled} onCheckedChange={(v) => set('dayuse_enabled', v)} data-testid="dayuse-enabled-switch" />
              </div>
              {data.dayuse_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-4 border-l border-primary/30">
                  <div className="space-y-2">
                    <Label>Heure de début</Label>
                    <Input type="time" value={data.dayuse_start_hour} onChange={(e) => set('dayuse_start_hour', e.target.value)} data-testid="dayuse-start-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Heure de fin</Label>
                    <Input type="time" value={data.dayuse_end_hour} onChange={(e) => set('dayuse_end_hour', e.target.value)} data-testid="dayuse-end-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix Day Use (€)</Label>
                    <Input type="number" step="1" value={data.dayuse_price} onChange={(e) => set('dayuse_price', e.target.value)} data-testid="dayuse-price-input" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HERO */}
        <TabsContent value="hero" className="mt-6 space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label>Tagline (sous-titre du hero)</Label>
                <Input value={data.tagline} onChange={(e) => set('tagline', e.target.value)} placeholder="Une parenthèse luxe & wellness" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Video className="w-4 h-4" /> URL Vidéo Hero (mp4)</Label>
                <Input value={data.hero_video_url} onChange={(e) => set('hero_video_url', e.target.value)} placeholder="https://..." data-testid="hero-video-input" />
                <p className="text-xs text-muted-foreground">Vidéo en autoplay sur la page d'accueil. Laissez vide pour utiliser l'image.</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><ImgIcon className="w-4 h-4" /> URL Image Hero</Label>
                <Input value={data.hero_image_url} onChange={(e) => set('hero_image_url', e.target.value)} placeholder="https://..." data-testid="hero-image-input" />
                {data.hero_image_url && <img src={data.hero_image_url} alt="Hero preview" className="mt-3 max-h-48 object-cover rounded-sm border border-border/40" />}
              </div>
              <div className="space-y-2">
                <Label>Image vignette (catalogue)</Label>
                <Input value={data.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="https://..." />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OPTIONS */}
        <TabsContent value="options" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">Sauna, jacuzzi, décoration romantique, départ tardif...</p>
            <Button variant="outline" onClick={() => set('options', [...(data.options || []), blankOption()])} data-testid="add-option-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un rituel
            </Button>
          </div>
          {(!data.options || data.options.length === 0) && (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Sparkles className="w-8 h-8 mx-auto mb-3 opacity-50" />Aucun rituel pour le moment.</CardContent></Card>
          )}
          <div className="space-y-4">
            {(data.options || []).map((opt, i) => (
              <Card key={opt.id} data-testid={`option-row-${i}`}>
                <CardContent className="pt-6 grid md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-3">
                    <Label className="text-xs">Nom</Label>
                    <Input value={opt.name} onChange={(e) => set('options', data.options.map(o => o.id === opt.id ? { ...o, name: e.target.value } : o))} placeholder="Sauna privé" />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">Description</Label>
                    <Input value={opt.description} onChange={(e) => set('options', data.options.map(o => o.id === opt.id ? { ...o, description: e.target.value } : o))} placeholder="Chaleur douce, bien-être" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Prix (€)</Label>
                    <Input type="number" value={opt.price} onChange={(e) => set('options', data.options.map(o => o.id === opt.id ? { ...o, price: parseFloat(e.target.value) || 0 } : o))} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Actif</Label>
                    <div className="h-9 flex items-center"><Switch checked={opt.enabled !== false} onCheckedChange={(v) => set('options', data.options.map(o => o.id === opt.id ? { ...o, enabled: v } : o))} /></div>
                  </div>
                  <div className="md:col-span-1 flex justify-end">
                    <Button variant="ghost" size="icon" onClick={() => set('options', data.options.filter(o => o.id !== opt.id))} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`remove-option-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="md:col-span-12">
                    <Label className="text-xs">Image (URL)</Label>
                    <Input value={opt.image_url} onChange={(e) => set('options', data.options.map(o => o.id === opt.id ? { ...o, image_url: e.target.value } : o))} placeholder="https://..." />
                    {opt.image_url && <img src={opt.image_url} alt={opt.name} className="mt-2 h-24 object-cover rounded-sm border border-border/40" />}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* MEDIA */}
        <TabsContent value="media" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">Photos et vidéos affichées dans la galerie publique.</p>
            <Button variant="outline" onClick={() => set('media', [...(data.media || []), blankMedia()])} data-testid="add-media-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un média
            </Button>
          </div>
          {(!data.media || data.media.length === 0) && (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><ImgIcon className="w-8 h-8 mx-auto mb-3 opacity-50" />Aucun média.</CardContent></Card>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {(data.media || []).map((m, i) => (
              <Card key={m.id} data-testid={`media-row-${i}`}>
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                    <select
                      value={m.type}
                      onChange={(e) => set('media', data.media.map(x => x.id === m.id ? { ...x, type: e.target.value } : x))}
                      className="bg-input border border-border/40 px-2 py-1 text-sm"
                    >
                      <option value="image">Photo</option>
                      <option value="video">Vidéo</option>
                    </select>
                    <Input
                      placeholder="https://..."
                      value={m.url}
                      onChange={(e) => set('media', data.media.map(x => x.id === m.id ? { ...x, url: e.target.value } : x))}
                      className="flex-1"
                    />
                    <Button variant="ghost" size="icon" onClick={() => set('media', data.media.filter(x => x.id !== m.id))} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    placeholder="Description (alt)"
                    value={m.alt || ''}
                    onChange={(e) => set('media', data.media.map(x => x.id === m.id ? { ...x, alt: e.target.value } : x))}
                  />
                  {m.url && m.type === 'image' && <img src={m.url} alt={m.alt} className="w-full h-40 object-cover rounded-sm border border-border/40" />}
                  {m.url && m.type === 'video' && <video src={m.url} controls className="w-full h-40 object-cover rounded-sm border border-border/40" />}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
